import { Client, type IMessage } from '@stomp/stompjs'
import { API_BASE, tokenStore } from './client'

/**
 * The live mission channel.
 *
 * <p>One socket per app, shared by every screen that watches a mission: opening
 * one per component would mean a client on the mission detail screen with the
 * chat open holds two, and the server authorises every subscription anyway.
 *
 * <p>Everything here is an optimisation over polling and is written to fail
 * quietly. A socket that cannot connect — a captive portal, a proxy that eats
 * upgrades, a browser with WebSockets off — must leave the screen working on
 * its REST refresh, not broken. So there is no error state exposed to callers:
 * `subscribeToMission` always returns an unsubscribe function, whether or not
 * anything is actually connected.
 */
export type MissionEvent =
  | {
      type: 'POSITION'
      missionId: string
      lat: number
      lon: number
      heading: number | null
      speedKmh: number | null
      accuracyM: number | null
      at: string
    }
  | { type: 'STATUS'; missionId: string; status: string; at: string }
  | {
      type: 'MESSAGE'
      missionId: string
      messageId: string
      senderId: string
      senderName: string
      body: string
      at: string
    }

type Handler = (event: MissionEvent) => void

/** `/v1` → `ws(s)://host/ws`, so the socket follows the API wherever it points. */
function socketUrl(): string {
  const base = new URL(API_BASE, window.location.origin)
  const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${base.host}/ws`
}

let client: Client | null = null
/** Mission id → the handlers watching it. */
const handlers = new Map<string, Set<Handler>>()
/** Mission id → the STOMP subscription, once one exists. */
const subscriptions = new Map<string, { unsubscribe: () => void }>()

function destination(missionId: string): string {
  return `/topic/missions/${missionId}`
}

function deliver(missionId: string, frame: IMessage) {
  const listeners = handlers.get(missionId)
  if (!listeners || listeners.size === 0) return
  let event: MissionEvent
  try {
    event = JSON.parse(frame.body) as MissionEvent
  } catch {
    // A frame we cannot read is not worth taking the socket down for.
    return
  }
  listeners.forEach((handler) => {
    handler(event)
  })
}

function subscribeNow(missionId: string) {
  if (!client?.connected || subscriptions.has(missionId)) return
  subscriptions.set(
    missionId,
    client.subscribe(destination(missionId), (frame) => {
      deliver(missionId, frame)
    }),
  )
}

function ensureClient() {
  if (client) return
  client = new Client({
    brokerURL: socketUrl(),
    // The access token is held in memory, never in localStorage, so it is read
    // at connect time rather than captured once: a reconnect after a refresh
    // must use the new token, not the expired one it was built with.
    beforeConnect: () => {
      client!.connectHeaders = { Authorization: `Bearer ${tokenStore.get() ?? ''}` }
    },
    reconnectDelay: 4000,
    heartbeatIncoming: 20000,
    heartbeatOutgoing: 20000,
    // The default logs every frame to the console, including the token.
    debug: () => {},
    onConnect: () => {
      // Re-subscribe on every connect, not only the first: a reconnect gets a
      // fresh session and the server remembers nothing about the old one.
      subscriptions.clear()
      handlers.forEach((_listeners, missionId) => {
        subscribeNow(missionId)
      })
    },
    onWebSocketClose: () => {
      subscriptions.clear()
    },
  })
  client.activate()
}

/**
 * Watch one mission. Returns the function that stops watching it.
 *
 * <p>Safe to call before the socket is up, and safe to call twice for the same
 * mission from two screens — the second gets its own handler on the one
 * subscription.
 */
export function subscribeToMission(missionId: string, handler: Handler): () => void {
  if (!tokenStore.get()) {
    // Not signed in: the server would refuse the CONNECT anyway, and opening a
    // socket that is certain to be rejected just makes noise in the console.
    return () => {}
  }

  const listeners = handlers.get(missionId) ?? new Set<Handler>()
  listeners.add(handler)
  handlers.set(missionId, listeners)

  ensureClient()
  subscribeNow(missionId)

  return () => {
    const current = handlers.get(missionId)
    current?.delete(handler)
    if (current && current.size === 0) {
      handlers.delete(missionId)
      subscriptions.get(missionId)?.unsubscribe()
      subscriptions.delete(missionId)
    }
    // The client itself is left running. Missions are opened and closed
    // constantly; tearing the socket down and rebuilding it each time costs a
    // handshake per navigation for no benefit.
  }
}

/** Closes the socket entirely. Called on sign-out, so a session leaves nothing open. */
export function closeLiveChannel() {
  handlers.clear()
  subscriptions.clear()
  void client?.deactivate()
  client = null
}
